<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useUISettings } from 'dashboard/composables/useUISettings';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const centralContext = ref('');
// A Central da IA tem rota própria por área (`/v2/ai-center/copilot`) desde o
// redesign; o endereço antigo com `?tab=` também continua valendo.
const isCentralPath = (path, accountId) => {
  const prefix = `/app/accounts/${accountId}/v2/ai-center`;
  return (
    typeof path === 'string' &&
    (path === prefix ||
      path.startsWith(`${prefix}?`) ||
      path.startsWith(`${prefix}/`))
  );
};
const centralReturnPath = computed(() =>
  isCentralPath(centralContext.value, route.params.accountId)
    ? centralContext.value
    : ''
);
watch(
  () => [
    route.params.accountId,
    route.query?.from_world,
    route.query?.return_to,
  ],
  () => {
    if (!centralReturnPath.value) centralContext.value = '';
    if (route.query?.from_world !== 'ai_center') return;
    const path = route.query.return_to;
    if (isCentralPath(path, route.params.accountId))
      centralContext.value = path;
  },
  { immediate: true }
);
const { isWorkspaceV2 } = useWorkspaceV2();
const contextKey = computed(() =>
  isWorkspaceV2.value
    ? `${route.params.accountId}:${route.params.assistantId || ''}`
    : undefined
);
const { uiSettings, updateUISettings } = useUISettings();

watch(
  () => route.params.assistantId,
  newAssistantId => {
    if (
      newAssistantId &&
      newAssistantId !== String(uiSettings.value.last_active_assistant_id)
    ) {
      updateUISettings({
        last_active_assistant_id: Number(newAssistantId),
      });
    }
  }
);
</script>

<template>
  <div class="flex w-full h-full min-h-0 flex-col" data-testid="page-captain">
    <div
      v-if="centralReturnPath"
      class="border-b border-n-weak bg-n-background px-4 py-2 sm:px-6"
    >
      <button
        type="button"
        class="inline-flex min-h-10 items-center gap-2 rounded-lg px-2 text-sm font-semibold text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        @click="router.push(centralReturnPath)"
      >
        <span class="i-ph-arrow-left size-4" aria-hidden="true" />
        {{ t('AI_CENTER.BACK_TO_CENTER') }}
      </button>
    </div>
    <section class="flex flex-1 h-full px-0 overflow-hidden bg-n-surface-1">
      <router-view :key="contextKey" />
    </section>
  </div>
</template>
